import { useRef, useState, useEffect } from 'react'

import cn from 'clsx'
import s from './styles.module.scss'
type Tab<T extends PropertyKey> = { name: string; value: T }

export const Tabs = <T extends PropertyKey>({
  activeTab,
  setActiveTab,
  tabs,
  tabClassName,
  tabsClassName,
  selectorClassName,
  tabsFillSpace,
  renderTab = {},
}: {
  activeTab: T
  setActiveTab: (val: T) => void
  tabs: Tab<T>[]
  tabsClassName?: string
  tabClassName?: string
  selectorClassName?: string
  tabsFillSpace?: boolean
  renderTab?: Partial<Record<T, (isActive: boolean, setPrevTab: () => void) => React.ReactNode>>
}) => {
  const prevActiveTab = useRef<T>(null)
  const tabRef = useRef<HTMLDivElement>(null)
  const [selectorWidth, setSelectorWidth] = useState(0)
  const [selectorX, setSelectorX] = useState(0)

  useEffect(() => {
    if (tabRef.current) {
      const rect = tabRef.current.getBoundingClientRect()
      const parentRect = tabRef.current.parentElement!.getBoundingClientRect()

      setSelectorWidth(rect.width)
      setSelectorX(rect.left - parentRect.left - 4)
    }
  }, [activeTab, renderTab])
  const setPrevTab = () => {
    if (prevActiveTab.current) {
      setActiveTab(prevActiveTab.current)
    }
  }
  const handleTabClick = (val: T) => {
    if (val === activeTab) return
    prevActiveTab.current = activeTab
    setActiveTab(val)
  }
  return (
    <div className={cn(s.tabs, tabsClassName)}>
      {tabs.map((tab) => (
        <div
          key={tab.name}
          ref={activeTab === tab.value && s.active ? tabRef : null}
          className={cn(s.tab, activeTab === tab.value && s.active, tabsFillSpace && s.fill, tabClassName)}
          onClick={() => handleTabClick(tab.value)}
        >
          {renderTab?.[tab.value]?.(activeTab === tab.value, setPrevTab) ?? tab.name}
        </div>
      ))}
      {selectorWidth && (
        <span
          className={cn(s.selector, selectorClassName)}
          style={{
            width: selectorWidth,
            transform: `translateX(${selectorX}px)`,
          }}
        />
      )}
    </div>
  )
}
